import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { fieldsValidators } from '@shared/constants/field-name-validators';
import { CrudOperation } from '@shared/models';
import { AppNotificationService, HeaderService } from '@shared/services';
import { notEmptyObjectValidator } from '@shared/validators/not-empty-object';
import { EntityService } from '../../../../core/services/entity/entity.service';
import { Entity } from '../../models/entity';

@Component({
  standalone: false,
  selector: 'gstar-add-entity',
  templateUrl: './add-entity.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddEntityComponent {
  private readonly entityService = inject(EntityService);
  private readonly fb = inject(FormBuilder);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly headerService = inject(HeaderService);
  private readonly translateService = inject(TranslateService);

  @Output() newEntityCreated = new EventEmitter<Entity>();
  public readonly maxNameLength = 255;

  public entityForm: FormGroup = this.fb.group({
    name: [
      '',
      [
        Validators.required,
        Validators.maxLength(this.maxNameLength),
        Validators.pattern(fieldsValidators.letterRequired),
      ],
    ],
    residencyJurisdiction: [{}, [notEmptyObjectValidator()]],
    entityType: [{}, [notEmptyObjectValidator()]],
    isActive: true,
  });

  destroyRef = inject(DestroyRef);

  @Input() showDrawer: boolean = false;
  @Output() showDrawerChange: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  visibleChange(visible: boolean): void {
    if (!visible) this.resetForm();
    this.showDrawerChange.emit(visible);
  }

  addEntity(): void {
    let isSuccess = false;
    const entity: Entity = this.entityForm.value;
    entity.clientId = this.headerService.selectedClient()?.id;

    this.entityService.create(entity).subscribe((createdEntity: Entity) => {
      isSuccess = true;

      this.appNotificationService.crudNotify(
        CrudOperation.Create,
        this.translateService.instant('upsertSecurity.addEntity'),
        isSuccess
      );
      this.entityService.reloadEntityList();
      this.resetForm();
      this.newEntityCreated.emit(createdEntity);
      this.showDrawerChange.emit(false);
    });
  }

  resetForm(): void {
    this.entityForm.patchValue({
      name: '',
      residencyJurisdiction: {},
      entityType: {},
    });
  }

  updateValues(model: FormGroup): void {
    this.entityForm = model;
  }
}
